import * as React from "react";
import { CHIP_REF_CLASS } from "./chip-extension";

/**
 * Per-type presentation for a rendered chip: text color, background tint, and
 * the inner markup of a 24×24 lucide icon used as a CSS mask. Pure data (no
 * runtime imports) so it stays server-safe and drives the injected CSS.
 */
export interface ChipTypeStyle {
  /** CSS `color` for the chip text and icon. */
  color: string;
  /** CSS `background` tint behind the chip. */
  background: string;
  /** Inner SVG markup (paths/circles) of the type's lucide icon, URL-encoded. */
  iconSvg: string;
}

/** SVG wrapper shared by every chip icon; only the inner markup varies. */
export function iconMaskUrl(iconSvg: ChipTypeStyle["iconSvg"]): string {
  const svg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' " +
    "fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E" +
    iconSvg +
    "%3C/svg%3E";
  return `url("data:image/svg+xml,${svg}")`;
}

/** Per-type color + icon CSS rules for a map of chip styles keyed by refType. */
export function chipTypeCss(styles: Record<string, ChipTypeStyle>): string {
  return Object.entries(styles)
    .map(([type, { color, background, iconSvg }]) => {
      const mask = iconMaskUrl(iconSvg);
      return `
      .${CHIP_REF_CLASS}[data-ref-type="${type}"] { color: ${color}; background: ${background}; }
      .${CHIP_REF_CLASS}[data-ref-type="${type}"]::before { mask-image: ${mask}; -webkit-mask-image: ${mask}; }`;
    })
    .join("");
}

/** lucide `triangle-alert`, shown on chips flagged invalid via chip state. */
const INVALID_CHIP_ICON = iconMaskUrl(
  "%3Cpath d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-2Z'/%3E%3Cpath d='M12 9v4'/%3E%3Cpath d='M12 17h.01'/%3E",
);

/**
 * Editor typography + base chip CSS shared by every chip editor surface.
 * `.chip-editor` scopes the editable input; `.chip-content` the read-only
 * renderer.
 */
export const CHIP_BASE_CSS = `
      .chip-editor .ProseMirror, .chip-content .ProseMirror {
        outline: none;
        min-height: inherit;
        overflow-wrap: break-word;
        word-break: break-word;
        font-size: 0.875rem;
        line-height: 1.4rem;
      }
      .chip-editor .ProseMirror p, .chip-content .ProseMirror p { margin: 0 0 0.5rem; }
      .chip-editor .ProseMirror p:last-child, .chip-content .ProseMirror p:last-child { margin-bottom: 0; }
      .chip-editor .ProseMirror h1, .chip-content .ProseMirror h1 {
        font-size: 1.5rem; font-weight: 700; line-height: 2rem; margin: 0.75rem 0 0.5rem;
      }
      .chip-editor .ProseMirror h2, .chip-content .ProseMirror h2 {
        font-size: 1.25rem; font-weight: 600; line-height: 1.75rem; margin: 0.5rem 0 0.375rem;
      }
      .chip-editor .ProseMirror h3, .chip-content .ProseMirror h3 {
        font-size: 1.125rem; font-weight: 600; line-height: 1.5rem; margin: 0.375rem 0 0.25rem;
      }
      .chip-editor .ProseMirror ul, .chip-content .ProseMirror ul { list-style: disc; padding-left: 1.5rem; margin: 0.25rem 0; }
      .chip-editor .ProseMirror ol, .chip-content .ProseMirror ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
      .chip-editor .ProseMirror li, .chip-content .ProseMirror li { margin: 0.125rem 0; }
      .chip-editor .ProseMirror li p, .chip-content .ProseMirror li p { margin: 0; }
      .chip-editor .ProseMirror blockquote, .chip-content .ProseMirror blockquote {
        border-left: 3px solid var(--border);
        padding-left: 1rem;
        margin: 0.5rem 0;
        color: var(--muted-foreground);
        font-style: italic;
      }
      .chip-editor .ProseMirror code, .chip-content .ProseMirror code {
        background-color: var(--muted);
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-size: 0.8125rem;
      }
      .chip-editor .ProseMirror pre, .chip-content .ProseMirror pre {
        background-color: var(--muted);
        padding: 0.75rem 1rem;
        border-radius: 0.375rem;
        margin: 0.5rem 0;
        overflow-x: auto;
      }
      .chip-editor .ProseMirror pre code, .chip-content .ProseMirror pre code {
        background: none;
        padding: 0;
        font-size: 0.8125rem;
      }
      .chip-editor .ProseMirror table, .chip-content .ProseMirror table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
        font-size: 0.875rem;
        margin: 0.375rem 0;
      }
      .chip-editor .ProseMirror th, .chip-editor .ProseMirror td,
      .chip-content .ProseMirror th, .chip-content .ProseMirror td {
        padding: 0.5rem 0.75rem;
        vertical-align: top;
        border-bottom: 1px solid color-mix(in oklch, var(--border) 60%, transparent);
      }
      .chip-editor .ProseMirror th, .chip-content .ProseMirror th {
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--muted-foreground);
        border-bottom-color: var(--border);
      }
      .chip-editor .ProseMirror p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: var(--muted-foreground);
        pointer-events: none;
        height: 0;
      }
      .${CHIP_REF_CLASS} {
        font-weight: 500;
        border-radius: 0.25rem;
        padding: 0.0625rem 0.3rem;
        box-decoration-break: clone;
        white-space: nowrap;
        font-size: inherit;
        cursor: default;
        display: inline-flex;
        align-items: center;
        vertical-align: baseline;
      }
      .${CHIP_REF_CLASS}::before {
        content: "";
        display: inline-block;
        width: 0.85em;
        height: 0.85em;
        margin-right: 0.22em;
        flex: none;
        background-color: currentColor;
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }
      a.${CHIP_REF_CLASS} { text-decoration: none; cursor: pointer; }
      a.${CHIP_REF_CLASS}:hover { text-decoration: underline; }
      /* Pointer cursor only where a click does something; the affordance ring
         marks every chip that opens a surface, hover-triggered ones included. */
      .chip-clickable .${CHIP_REF_CLASS} { cursor: pointer; }
      .chip-interactive .${CHIP_REF_CLASS}:hover {
        box-shadow: inset 0 0 0 1px color-mix(in oklab, currentColor 40%, transparent);
      }
      /* Invalid chip state (set via data-chip-invalid decorations). The
         doubled attribute selector out-specifies per-type color/icon rules,
         which are injected after this base sheet. */
      .${CHIP_REF_CLASS}[data-chip-invalid][data-chip-invalid] {
        color: color-mix(in oklab, var(--destructive) 80%, black);
        background: color-mix(in oklab, var(--destructive) 12%, transparent);
        box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--destructive) 45%, transparent);
      }
      .${CHIP_REF_CLASS}[data-chip-invalid][data-chip-invalid]::before {
        mask-image: ${INVALID_CHIP_ICON}; -webkit-mask-image: ${INVALID_CHIP_ICON};
      }
      .chip-editor .ProseMirror ul[data-type="taskList"],
      .chip-content .ProseMirror ul[data-type="taskList"] {
        list-style: none;
        padding-left: 0;
      }
      .chip-editor .ProseMirror ul[data-type="taskList"] li,
      .chip-content .ProseMirror ul[data-type="taskList"] li {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding-left: 0;
      }
      .chip-editor .ProseMirror ul[data-type="taskList"] li > label,
      .chip-content .ProseMirror ul[data-type="taskList"] li > label {
        margin-top: 0.2rem;
      }
      .chip-editor .ProseMirror ul[data-type="taskList"] li > div,
      .chip-content .ProseMirror ul[data-type="taskList"] li > div {
        flex: 1;
        min-width: 0;
      }
    `;

/**
 * Compact Linear type scale for `variant="seamless"`. Numbers match compact
 * MarkdownRenderer (`text-sm` / `text-lg` / `mt-2.5` …) so focusing the
 * task description cannot change heading size, list indent, or paragraph
 * rhythm. Must not be applied to boxed chip surfaces.
 */
export const EDITOR_MARKDOWN_CSS = `
  .markdown-editor .ProseMirror {
    outline: none;
    min-height: inherit;
    overflow-wrap: anywhere;
  }
  .markdown-editor .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: var(--muted-foreground);
    pointer-events: none;
    height: 0;
  }
  .markdown-editor .ProseMirror strong { font-weight: 700; }
  .markdown-editor .ProseMirror em { font-style: italic; }
  .markdown-editor .ProseMirror s { text-decoration: line-through; }

  .markdown-editor--boxed .ProseMirror {
    word-break: break-word;
    overflow-wrap: break-word;
    font-size: 0.875rem;
    line-height: 1.4rem;
  }
  .markdown-editor--boxed .ProseMirror p { margin: 0; }
  .markdown-editor--boxed .ProseMirror h1 {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 2rem;
    margin: 0.75rem 0 0.5rem;
  }
  .markdown-editor--boxed .ProseMirror h2 {
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
    margin: 0.5rem 0 0.375rem;
  }
  .markdown-editor--boxed .ProseMirror h3 {
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.5rem;
    margin: 0.375rem 0 0.25rem;
  }
  .markdown-editor--boxed .ProseMirror ul {
    list-style-type: disc;
    padding-left: 1.5rem;
    margin: 0.25rem 0;
  }
  .markdown-editor--boxed .ProseMirror ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
    margin: 0.25rem 0;
  }
  .markdown-editor--boxed .ProseMirror li > p { margin: 0; }
  .markdown-editor--boxed .ProseMirror blockquote {
    border-left: 3px solid var(--border);
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: var(--muted-foreground);
    font-style: italic;
  }
  .markdown-editor--boxed .ProseMirror a {
    color: var(--primary);
    text-decoration: underline;
  }
  .markdown-editor--boxed .ProseMirror code {
    background-color: var(--muted);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.8125rem;
  }
  .markdown-editor--boxed .ProseMirror pre {
    background-color: var(--muted);
    padding: 0.75rem 1rem;
    border-radius: 0.375rem;
    margin: 0.5rem 0;
    overflow-x: auto;
  }
  .markdown-editor--boxed .ProseMirror pre code {
    background: none;
    padding: 0;
    font-size: 0.8125rem;
  }

  .markdown-editor--seamless .ProseMirror {
    font-size: 0.875rem;
    line-height: 1.625;
    color: var(--foreground);
  }
  .markdown-editor--seamless .ProseMirror > *:first-child { margin-top: 0; }
  .markdown-editor--seamless .ProseMirror > *:last-child { margin-bottom: 0; }
  .markdown-editor--seamless .ProseMirror p {
    margin: 0;
    color: var(--foreground);
  }
  .markdown-editor--seamless .ProseMirror p + p { margin-top: 0.25rem; }
  .markdown-editor--seamless .ProseMirror h1 {
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.75rem;
    margin: 0.625rem 0 0.25rem;
    color: var(--foreground);
  }
  .markdown-editor--seamless .ProseMirror h2 {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.5rem;
    margin: 0.5rem 0 0.25rem;
    color: var(--foreground);
  }
  .markdown-editor--seamless .ProseMirror h3 {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    margin: 0.375rem 0 0.125rem;
    color: var(--foreground);
  }
  .markdown-editor--seamless .ProseMirror ul {
    list-style-type: disc;
    list-style-position: outside;
    margin: 0.125rem 0;
    padding-left: 1rem;
  }
  .markdown-editor--seamless .ProseMirror ol {
    list-style-type: decimal;
    list-style-position: outside;
    margin: 0.125rem 0;
    padding-left: 1rem;
  }
  .markdown-editor--seamless .ProseMirror li {
    padding-left: 0.25rem;
  }
  /* Keep the list-item paragraph in the box. Flattening it with display:contents
     matches the compact renderer visually but drops the node ProseMirror maps
     the caret onto, so Enter / Backspace / nest stop behaving like a list. */
  .markdown-editor--seamless .ProseMirror li > p { margin: 0; }
  .markdown-editor--seamless .ProseMirror li > ul,
  .markdown-editor--seamless .ProseMirror li > ol { margin-top: 0.25rem; }
  .markdown-editor .ProseMirror ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;
  }
  .markdown-editor .ProseMirror ul[data-type="taskList"] li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding-left: 0;
  }
  .markdown-editor .ProseMirror ul[data-type="taskList"] li > label {
    margin-top: 0.2rem;
  }
  .markdown-editor .ProseMirror ul[data-type="taskList"] li > div {
    flex: 1;
    min-width: 0;
  }
  .markdown-editor--seamless .ProseMirror blockquote {
    border-left: 4px solid var(--border);
    padding-left: 1rem;
    margin: 0.375rem 0;
    color: var(--muted-foreground);
    font-style: italic;
  }
  .markdown-editor--seamless .ProseMirror hr {
    border: 0;
    border-top: 1px solid var(--border);
    margin: 0.5rem 0;
  }
  .markdown-editor--seamless .ProseMirror a {
    color: var(--foreground);
    text-decoration: underline;
    text-underline-offset: 4px;
    overflow-wrap: anywhere;
  }
  .markdown-editor .ProseMirror a.quovy-public-id-link {
    cursor: pointer;
    text-decoration-style: dotted;
  }
  .markdown-editor--seamless .ProseMirror code {
    background-color: var(--muted);
    color: var(--foreground);
    border: 1px solid var(--border);
    border-radius: 0.25rem;
    padding: 0.125rem 0.25rem;
    font-size: 0.75rem;
  }
  .markdown-editor--seamless .ProseMirror pre {
    background-color: var(--muted);
    padding: 0.75rem 1rem;
    border-radius: 0.375rem;
    margin: 0.375rem 0;
    overflow-x: auto;
    overflow-wrap: normal;
  }
  .markdown-editor--seamless .ProseMirror pre code {
    background: none;
    border: 0;
    padding: 0;
    font-size: 0.75rem;
  }
  .markdown-editor--seamless .ProseMirror table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    font-size: 0.875rem;
    margin: 0.375rem 0;
  }
  .markdown-editor--seamless .ProseMirror th,
  .markdown-editor--seamless .ProseMirror td {
    padding: 0.5rem 0.75rem;
    vertical-align: top;
    border-bottom: 1px solid color-mix(in oklch, var(--border) 60%, transparent);
  }
  .markdown-editor--seamless .ProseMirror th {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted-foreground);
    border-bottom-color: var(--border);
  }
  .markdown-editor--seamless .ProseMirror tr:last-child th,
  .markdown-editor--seamless .ProseMirror tr:last-child td {
    border-bottom: 0;
  }
`;

/** Per-refType chip styling injected once per `id` (color + icon rules). */
export interface ChipStylesConfig {
  /** DOM id for the injected `<style>` element — stable per chip family. */
  id: string;
  styles: Record<string, ChipTypeStyle>;
  /** Family-specific rules appended after the per-type rules, so they win ties. */
  extraCss?: string;
}

/** Memoized {@link chipTypeCss} for an optional style config, so the CSS
 *  string (and its icon-mask SVG encoding) is rebuilt only when the config
 *  identity changes, not on every render. */
export function useChipTypeCss(chipStyles?: ChipStylesConfig): string {
  return React.useMemo(
    () => (chipStyles ? chipTypeCss(chipStyles.styles) + (chipStyles.extraCss ?? "") : ""),
    [chipStyles],
  );
}

/** Injects `css` into the document head exactly once per `id`. No-ops when
 *  either argument is empty (lets callers pass optional style configs). */
export function useInjectedStyles(id: string, css: string): void {
  React.useEffect(() => {
    if (!id || !css) return;
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
  }, [id, css]);
}
